import _ from "lodash";

import Sentry from "@similarweb/sentry-logger";

import { isHidden, isLocked } from "app/common/services/pageClaims";
import { swSettings } from "app/common/services/swSettings";
import { SEGMENTS_FAMILY_NAME } from "app/components/widget/widget-utilities/segments/constants";
import CountryService from "app/services/CountryService";
import { periodOverPeriodService } from "app/services/PeriodOverPeriodService";

import { findLastRowFromWidgets } from "../../../pages/dashboard/GridUtilities-v2";
import * as GridUtilitiesV2 from "../../../pages/dashboard/GridUtilities-v2";
import InvestorsWorkspaceApiService from "../../../services/workspaces/investorsWorkspaceApiService";
import SalesWorkspaceApiService from "../../../services/workspaces/salesWorkspaceApiService";
import type { IWidgetKeyBase } from "../../widget/widget-types/Widget";
import DashboardService from "../services/DashboardService";
import { getMetricProperties } from "../widget-wizard/utils/dashboardWizardUtils";

import DashboardTemplateGetter from "./StaticDashboardTemplateGetter";
import BlankDashboard from "./templates/BlankDashboard";
import type { IDashboardTemplate, IDashboardTemplateMetaData, IWidgetTemplate } from "./types";

export interface IWidgetProperties {
    country: number;
    customAsset: boolean;
    dataModel: any;
    duration: string;
    family: string;
    filters: any;
    metric: string;
    key: Array<{
        name: string;
        image: string;
        type: string;
    }>;
    options: any;
    selectedChannel: string;
    type: string;
    webSource: string;
    width: number;
}

export enum EDashboardParentType {
    TEMPLATE = "Template",
    // WORKSPACE = "Workspace"
}

export enum EDashboardOriginType {
    INVESTORS_WORKSPACE = "InvestorsWorkspace",
    SALES_WORKSPACE = "SalesWorkspace",
}

type dashboardTemplateGetter = () => Promise<IDashboardTemplate[]>;

const MAX_COLS = 4;

export interface IDashboardTemplateService {
    templates: IDashboardTemplate[];
    investorsApiService: InvestorsWorkspaceApiService;
    salesApiService: SalesWorkspaceApiService;
    saveDashboardTemplate(templates: IDashboardTemplate, parentType?: EDashboardParentType): Promise<any>;

    linkDashboardToOrigin(originType: EDashboardOriginType, originId: string, dashboardId: string): Promise<any>;

    generateDashboardTemplates(dashboardTemplateGetter: dashboardTemplateGetter): Promise<IDashboardTemplateMetaData[]>;

    getTemplateById(id: string | number): IDashboardTemplate;

    populateTemplate(
        keys: IWidgetKeyBase[],
        country: number,
        template: IDashboardTemplate,
        isSegmentsGroup?: boolean,
        duration?: string,
    ): IDashboardTemplate;

    cloneTemplate(template: IDashboardTemplate): IDashboardTemplate;
}

const convertDashboardToJSON = (
    dashboard,
    parentType?: EDashboardParentType,
    parentId?: string,
    segmentsGroupId?: string,
    segmentId?: string,
    customIndustryId?: string,
) => {
    return {
        title: dashboard.title,
        widgets: dashboard.widgets.map((widget) => {
            const properties = widget.properties;

            if (dashboard?.familyType === SEGMENTS_FAMILY_NAME) {
                properties.filters = {
                    ...properties?.filters,
                    segmentsGroupId,
                };
            }

            return {
                pos: JSON.stringify(_.omit(widget.pos, ["oldRow", "oldCol"])),
                properties: JSON.stringify(properties),
                segmentsGroupId,
                segmentId,
                ...(customIndustryId && { customIndustryId }),
            };
        }),
        ParentId: parentId,
        ParentType: parentType,
    };
};

class DashboardTemplateServiceClass implements IDashboardTemplateService {
    templates: IDashboardTemplate[];

    investorsApiService: InvestorsWorkspaceApiService;

    salesApiService: SalesWorkspaceApiService;

    constructor() {
        this.investorsApiService = new InvestorsWorkspaceApiService();
        this.salesApiService = new SalesWorkspaceApiService();
    }

    /**
     * Save dashboards to the DB
     * @param dashboards
     * @returns {IPromise<T> | any}
     */
    public saveDashboardTemplate(
        dashboard,
        parentType?: EDashboardParentType,
        parentId?: string,
        segmentsGroupId?: string,
        segmentId?: string,
        customIndustryId?: string,
    ) {
        const dashboardsJSON = convertDashboardToJSON(
            dashboard,
            parentType,
            parentId,
            segmentsGroupId,
            segmentId,
            customIndustryId,
        );
        return DashboardService.bulkAddDashboards([dashboardsJSON]);
    }

    /**
     * A callback to be triggered after dashboard templated created, only if originType and originId was provided.
     * @param originType
     * @param originId
     * @param dashboardId
     */
    public linkDashboardToOrigin(originType: EDashboardOriginType, originId: string, dashboardId: string) {
        switch (originType) {
            case EDashboardOriginType.INVESTORS_WORKSPACE:
                return this.investorsApiService.linkInvestorsOpportunityListToDashboard(originId, dashboardId);
            case EDashboardOriginType.SALES_WORKSPACE:
                return this.salesApiService.linkSalesOpportunityListToDashboard(originId, dashboardId);
        }
    }

    /**
     * Generates template based on templates from `dashboardTemplateGetter`.
     * @param {dashboardTemplateGetter} dashboardTemplateGetter
     * @returns {Promise<[any]>}
     */
    public async generateDashboardTemplates(
        dashboardTemplateGetter: dashboardTemplateGetter = DashboardTemplateGetter.getDashboardTemplates,
    ) {
        const rawTemplates = await this.loadDashboardTemplate(dashboardTemplateGetter);
        const templatesList = rawTemplates.filter((template) => !template.isHidden);
        const components = templatesList.map((template) => {
            const widgets = template.widgets;
            return widgets.map((widget) => {
                const { metric, family } = widget.properties;
                const metricProperties = getMetricProperties(metric, family);
                const componentId = metricProperties.component;
                return componentId;
            });
        });
        await swSettings.verifyOrFetchComponentById(
            Array.from(new Set(components.flat().filter((componentId): componentId is string => !!componentId))),
        );

        const templates = await Promise.all(
            templatesList.map(async (template) => {
                const isDashboardValid = await this.validateDashboard(template);
                template.locked = !isDashboardValid;
                return template;
            }),
        );
        templates.unshift(BlankDashboard);
        return templates;
    }

    /**
     * Populate each widget in the template with country and key
     * @param {string[]} key
     * @param {number} country
     * @param {dashboardTemplate} template
     * @param {isSegmentsGroup} boolean
     * @returns {dashboardTemplate}
     */
    public populateTemplate(
        keys: IWidgetKeyBase[],
        country: number,
        template: IDashboardTemplate,
        isSegmentsGroup: boolean,
        duration: string | null,
    ) {
        let widgets = [];
        let newRowsCounter = 0;
        const filteredWidgetsBywebsource = this.filterByWebsource(template.widgets, country);
        const filteredWidgets = this.filterByCountry(filteredWidgetsBywebsource, country);
        filteredWidgets.forEach((widget, index) => {
            const copies = [];
            // const indexToPushCopies = index + _.result(widget, 'properties.multipleOffset', 0);
            widget.pos.sizeY = widget.pos.sizeY || 1;
            widget.properties.country = _.result(widget, "properties.fixedCountry", country);
            widget.properties.key = widget.singleModeOnly ? [keys[0]] : keys;

            if (isSegmentsGroup) {
                widget.properties.filters.selectedSegmentsId = keys;
                widget.properties.duration = duration;
            }

            // If pre defined title - set custom title template to prevent other services to manipulate the title
            if (widget.properties.title) {
                widget.properties.titleTemplate = "custom";
            }
            if (widget.properties.multiple && keys.length > 1) {
                const keysToAdd = widget.singleModeOnly ? keys.slice(1) : keys;
                keysToAdd.forEach((key, i) => {
                    // create a copy of the original widget and set the key to a single key
                    const copy = {
                        pos: {
                            ...widget.pos,
                            ..._.result(widget, "properties.multipleOverrides.pos", {}),
                            col: null,
                            row: null,
                        },
                        properties: {
                            ...widget.properties,
                            ..._.result(widget, "properties.multipleOverrides.properties", {}),
                        },
                    };
                    copy.properties.key = [key];
                    copies.push(copy);
                });
            }
            // Since this is a template and the row values depend on the number of keys (urls), the row shoudl be adjusted to consider new lines added
            widgets = GridUtilitiesV2.putItem(widget, widgets, newRowsCounter);

            // push copies
            _.forEach(copies, (copy) => {
                const currentRows = findLastRowFromWidgets(widgets);
                widgets = GridUtilitiesV2.putItem(copy, widgets);
                // calculate if new rows were created
                newRowsCounter += findLastRowFromWidgets(widgets) - currentRows;
            });

            if (widget.properties.breakRowAfterCopies) {
                newRowsCounter++;
            }
        });

        const tmpLayout = GridUtilitiesV2.translateWidgetsToLayout(widgets);
        const newLayout = GridUtilitiesV2.removeEmptyLinesFromLayout(tmpLayout);
        const newWidgets = GridUtilitiesV2.buildWidgetsFromLayout(newLayout, widgets);
        template.widgets = newWidgets;
        return template;
    }

    /**
     * Get a single template, include it's widgets
     * @param {string | number} id
     * @returns {any}
     */
    public getTemplateById(id: string | number) {
        const template = _.find<IDashboardTemplate>(this.templates, { id: id });
        return template;
    }

    /**
     * Validate if at least one widgets in the dashboard is invalid
     * @param dashboardTemplate
     * @returns {boolean}
     */
    private async validateDashboard(dashboardTemplate) {
        const widgets = dashboardTemplate.widgets;
        return await widgets.every(async (widget) => this.validateWidget(widget, dashboardTemplate.id));
    }

    /**
     * Validate a single widget model
     * @param {IWidgetModel} widget
     * @returns {boolean}
     */
    private async validateWidget(widget: any, templateId?: string | number) {
        let isValid = true;

        const { metric, family, duration, flexibleDuration } = widget.properties;
        const metricProperties = getMetricProperties(metric, family);
        const comparedDuration = _.result(widget, "properties.multipleOverrides.properties.comparedDuration", false);
        const componentId = metricProperties.component;

        if (!componentId) {
            return false;
        }

        // make sure we have the full component config before we move forward
        await swSettings.verifyOrFetchComponentById([componentId]);

        // componentId can be missing from swSettings.components if the backend didn't return it
        // (e.g. a legacy/removed component referenced by an old dashboard template)
        const componentConfig = swSettings.components[componentId];
        if (!componentConfig) {
            Sentry.errorTracker({
                message: "DashboardTemplateService.validateWidget: componentId is missing from swSettings.components",
                e: new Error("Component config missing from swSettings.components"),
                level: "error",
                params: {
                    extra: { componentId, metric, family, templateId },
                },
            });
        }
        const isComponentAllowed = componentConfig?.isAllowed ?? false;

        // This can override isComponentAllowed = true, causing this method to return isValid = false.
        const isWidgetAllowed =
            !isLocked(swSettings.components[metricProperties.availabilityComponent]) &&
            !isHidden(swSettings.components[metricProperties.availabilityComponent]);

        const isDurationAllowed = swSettings.allowedDuration(duration, componentId);
        const isCustomDurationAllowed = comparedDuration
            ? periodOverPeriodService.periodOverPeriodEnabled(comparedDuration, duration, [{}], componentId)
            : true;

        if (!isCustomDurationAllowed) {
            widget.properties.multipleOverrides.properties.comparedDuration = "1m";
        }

        // currently validate that component is allowed, widget is allowed and duration is allowed
        if (!isDurationAllowed) {
            // change widget's duration to be the maximum the user has
            if (flexibleDuration) {
                // sim-30775: take the max duration the user have (from the presets)
                const presets = [...(componentConfig?.datePickerPresets ?? [])].reverse();
                const maxDurationAllowed = presets.find((preset) => !preset.locked)?.value;
                widget.properties.duration = maxDurationAllowed;
            } else {
                return false;
            }
        }
        if (!isComponentAllowed) {
            isValid = false;
        }
        if (!isWidgetAllowed) {
            isValid = false;
        }

        return isValid;
    }

    private async loadDashboardTemplate(
        dashboardTemplateGetter: dashboardTemplateGetter,
    ): Promise<IDashboardTemplate[]> {
        const rawDashboards = await dashboardTemplateGetter();
        this.templates = rawDashboards;
        return this.templates;
    }

    /**
     * remove widgets or change their websource from total to desktop if needed
     * @param {IWidgetTemplate[]} widgets
     * @param country
     * @returns {any[]}
     */
    private filterByWebsource(widgets: IWidgetTemplate[], country: number) {
        const result = [];
        const countryHasMobileWeb = swSettings.allowedCountry(country, "MobileWebSearch");
        const userHasMobileWeb = swSettings.components.MobileWeb.isAllowed;
        widgets.forEach((widget) => {
            if (!countryHasMobileWeb || !userHasMobileWeb) {
                if (widget.properties.mobileWebOnly) {
                    return;
                }
            }

            if (!countryHasMobileWeb) {
                this.handleCountryNoMobileWeb(widget);
            } else if (!userHasMobileWeb) {
                this.handleUserNoMobileWeb(widget);
            }

            result.push(widget);
        });

        return result;
    }

    /**
     * remove widgets if needed
     * @param {IWidgetTemplate[]} widgets
     * @param country
     * @returns {any[]}
     */
    private filterByCountry(widgets: IWidgetTemplate[], country: number) {
        const result = [];
        const isUsState = CountryService.isUSState(country);
        widgets.forEach((widget) => {
            const metricProperties = getMetricProperties(widget.properties.metric, widget.properties.family);
            const componentId = metricProperties.component;
            const isUSStatesSupportedByComponent = swSettings.components[componentId]?.resources?.USStateSupported;
            if (
                isUsState &&
                !isUSStatesSupportedByComponent &&
                widget.properties.options?.removeIfCountryNotSupported
            ) {
                return;
            }
            result.push(widget);
        });

        return result;
    }

    private handleCountryNoMobileWeb(widget) {
        const webSource = widget.properties.webSource;
        if (webSource === "Total") {
            widget.properties.webSource = "Desktop";
            if (widget.properties.title) {
                widget.properties.title = widget.properties.title.replace("Total", "Desktop");
            }
        }
    }

    private handleUserNoMobileWeb(widget) {
        const webSource = widget.properties.webSource;
        if (webSource === "Desktop" && widget.properties.froWebSource) {
            widget.properties.webSource = widget.properties.froWebSource;
            widget.properties.title = widget.properties.title.replace("Desktop", widget.properties.froWebSource);
        }
    }

    public cloneTemplate(template: IDashboardTemplate) {
        return _.cloneDeep(template);
    }
}

export const DashboardTemplateService = new DashboardTemplateServiceClass();

export type { IDashboardTemplate, IDashboardTemplateMetaData, IWidgetTemplate, IWidgetPos } from "./types";
